import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE_PRICE = 39;

export default function PricingPage() {
  const paypalRef = React.useRef(null);
  const [paypalLoaded, setPaypalLoaded] = React.useState(false);
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [validating, setValidating] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const canceledToastFired = React.useRef(false);
  React.useEffect(() => {
    if (searchParams.get("canceled") === "1" && !canceledToastFired.current) {
      canceledToastFired.current = true;
      // Defer past first paint so Sonner's Toaster listener is registered.
      setTimeout(() => {
        toast.error("Checkout canceled — you have not been charged.");
      }, 150);
    }
  }, [searchParams]);

  React.useEffect(() => {
    const renderPayPal = () => {
      if (window.paypal && paypalRef.current && !paypalLoaded) {
        try {
          window.paypal.HostedButtons({ hostedButtonId: "PMBMH66BV9KR8" }).render(paypalRef.current);
          setPaypalLoaded(true);
        } catch (e) { /* PayPal render pending */ }
      }
    };
    renderPayPal();
    const timer = setInterval(renderPayPal, 1000);
    return () => clearInterval(timer);
  }, [paypalLoaded]);

  const applyCode = async () => {
    setCodeError("");
    if (!code.trim()) { setCodeError("Enter a discount code"); return; }
    setValidating(true);
    try {
      const res = await axios.post(`${API}/payments/validate-code`, { code: code.trim() });
      setAppliedCode(res.data);
      setCodeError("");
    } catch (err) {
      setAppliedCode(null);
      setCodeError(err.response?.data?.detail || "Invalid discount code");
    }
    setValidating(false);
  };

  const removeCode = () => {
    setAppliedCode(null);
    setCode("");
    setCodeError("");
  };

  const startStripeCheckout = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please sign in to upgrade to lifetime.");
      window.location.assign("/login?next=/pricing");
      return;
    }
    if (isFree) {
      toast.error("Your discount is 100% — no checkout needed. Use the Redeem Code panel on your dashboard.");
      return;
    }
    setStripeLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/stripe/create-session`,
        {
          package_id: "lifetime_standard",
          origin_url: window.location.origin,
          discount_code: appliedCode?.code || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) {
        window.location.assign(res.data.checkout_url);
      } else {
        toast.error("Could not start Stripe checkout — please try PayPal.");
        setStripeLoading(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start Stripe checkout.";
      toast.error(msg);
      setStripeLoading(false);
    }
  };

  const discount = appliedCode ? appliedCode.pct : 0;
  const finalPrice = appliedCode ? appliedCode.final_price.toFixed(2) : BASE_PRICE.toFixed(2);
  const isFree = discount === 100;

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 neon-text-white" data-testid="pricing-title">Early Adopters' Special</h1>
            <p className="neon-white-text text-lg mb-2">Limited to the first 10,000 users</p>
            <p className="text-[#E8E4DC]">One price. Lifetime access. No subscriptions.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto" data-testid="pricing-grid">
            {/* Trial */}
            <div className="card-glass p-8" data-testid="pricing-trial">
              <div className="text-sm text-cyan-400 font-semibold mb-2 uppercase tracking-wider">Try It First</div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-2">72-Hour Trial</h3>
              <div className="text-4xl font-bold text-white mb-1">Free</div>
              <p className="text-[#E8E4DC] text-sm mb-6">Full features for 72 hours</p>
              <ul className="space-y-3 mb-8 text-[#E8E4DC]">
                {["All premium features unlocked","Chrome & Edge extension","Desktop app (Windows/Mac)","Mobile app (iOS & Android)","No credit card required"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><span className="text-cyan-400">&#10003;</span> {t}</li>
                ))}
                <li className="flex items-center gap-2"><span className="text-[#E8E4DC]">&#8987;</span> <span className="text-[#E8E4DC]">Expires after 72 hours</span></li>
              </ul>
              <Link to="/register" className="btn-secondary w-full block text-center" data-testid="pricing-trial-btn">Start Free Trial</Link>
            </div>
            {/* Lifetime */}
            <div className="card-glass p-8 gradient-border relative" data-testid="pricing-lifetime">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#A3E635] text-black px-4 py-1 rounded-full text-sm font-bold whitespace-nowrap">Early Adopters' Special</div>
              <div className="text-sm text-[#A3E635] font-semibold mb-2 uppercase tracking-wider">Lifetime Access</div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-2">Top Screen&#8482;</h3>
              <div className="flex items-baseline gap-2 mb-1">
                {appliedCode ? (
                  <>
                    <span className="text-5xl font-bold text-white">{isFree ? "Free" : `$${finalPrice}`}</span>
                    {!isFree && <span className="text-[#E8E4DC] text-sm">one-time</span>}
                    <span className="text-lg text-[#E8E4DC] line-through ml-1">${BASE_PRICE}</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-white">${BASE_PRICE}</span>
                    <span className="text-[#E8E4DC] text-sm">one-time</span>
                  </>
                )}
              </div>
              {appliedCode && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#A3E635]/20 text-[#A3E635]" data-testid="discount-badge">
                    {appliedCode.label} &mdash; {appliedCode.code}
                  </span>
                  <button onClick={removeCode} className="text-[#E8E4DC] hover:text-red-400 text-xs transition-colors" data-testid="remove-code">remove</button>
                </div>
              )}
              <p className="text-[#A3E635] text-sm mb-6">Lifetime updates &amp; upgrades included</p>
              <ul className="space-y-3 mb-6 text-[#E8E4DC]">
                {["All premium features — forever","Chrome & Edge extension","Desktop app (Windows/Mac)","Mobile app (iOS & Android)","Lifetime updates & upgrades","Priority support"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><span className="text-[#A3E635]">&#10003;</span> {t}</li>
                ))}
              </ul>

              {/* Discount Code Input */}
              {!appliedCode && (
                <div className="mb-4" data-testid="discount-section">
                  <div className="flex gap-2">
                    <input
                      value={code}
                      onChange={(e) => { setCode(e.target.value); setCodeError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && applyCode()}
                      className="input-dark flex-1 text-sm"
                      placeholder="Discount code"
                      data-testid="discount-input"
                    />
                    <button onClick={applyCode} disabled={validating} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 text-[#E8E4DC] hover:bg-white/20 hover:text-white border border-white/10 transition-colors disabled:opacity-50" data-testid="apply-code-btn">
                      {validating ? "..." : "Apply"}
                    </button>
                  </div>
                  {codeError && <p className="text-red-400 text-xs mt-1.5" data-testid="code-error">{codeError}</p>}
                </div>
              )}

              <button
                type="button"
                onClick={startStripeCheckout}
                disabled={stripeLoading}
                className="btn-primary w-full mb-2 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pricing-stripe-checkout"
              >
                {stripeLoading ? "Redirecting…" : `Pay with Card — $${finalPrice}`}
              </button>
              <p className="text-center text-[#E8E4DC] text-[11px] mb-3" data-testid="stripe-methods-note">
                Cards, Apple Pay &amp; Google Pay via Stripe
              </p>

              <div className="flex items-center gap-3 my-2" aria-hidden="true">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-[#E8E4DC]/60 text-[10px] uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              <div ref={paypalRef} id="paypal-container-PMBMH66BV9KR8" className="mb-3" data-testid="paypal-btn-container"></div>
              <p className="text-center text-[#E8E4DC] text-xs">Secure payment via PayPal &amp; Venmo</p>
            </div>
          </div>
          <div className="mt-16 text-center">
            <div className="card-glass p-6 max-w-2xl mx-auto">
              <h3 className="text-white font-semibold mb-3">Why Early Adopters' Pricing?</h3>
              <p className="text-[#E8E4DC] text-sm leading-relaxed">
                We're offering the first 10,000 users a one-time lifetime price of <span className="text-[#A3E635] font-bold">$39</span> — 
                that includes every future update, upgrade, and new feature. No subscriptions, no recurring charges. 
                Once this offer is claimed, the price will increase to a monthly subscription model.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
